"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDescendingGroupRecuriveQuery = exports.getItemTypeToGroupNestedDescendingQueryDef = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const EdgeTypeMatrix_1 = require("../classDependentTypes/EdgeTypes/EdgeTypeMatrix");
const edgeTypeMatrixFns_1 = require("../classDependentTypes/EdgeTypes/edgeTypeMatrixFns");
const BranchingQueryDefinition_1 = require("../util/dynamodb/GraphQuery/BranchingQueryDefinition");
const debug_1 = require("../util/lambda/debug");
const simple_1 = require("./simple");
const debug = debug_1.DEBUG_OVERRIDE || (debug_1.NOT_LAMBDA && false);
/** special key used to tag final query path so that additional queries can be added */
const GROUP_SUPPORTED_TYPE_QUERY_KEY = 'groupToSupportedTypeQuery';
/** limit to nodes with correct supportedType */
const getGroupSupportedTypeFilter = (supportedType) => [
    {
        setOperation: iotv_v3_types_1.LogicalOperatorType.AND,
        filters: [
            {
                key: 'supportedType',
                predicate: iotv_v3_types_1.ComparisonType.EQUALS,
                value: supportedType,
            },
        ],
    },
];
/** limit to sub groups with correct supported type */
const getGroupSubGroupParams = (supportedType) => [
    {
        objectTypeId: 'Group',
        adjacencyType: iotv_v3_types_1.AdjacentType.CHILD,
        edgeTypeId: EdgeTypeMatrix_1.edgeTypeMatrix.Group.Group.group_has_subGroup.edgeTypeId,
        nodeFilters: getGroupSupportedTypeFilter(supportedType),
    },
];
/** returns a query definition which will recursively navigate down the group structure from defining type to supported type, returning all groups and, optionally, the instances inside the group.
 * WARN: BY DESIGN does not return supported types or groups of supported types that belong to children of defining type in group of defining type under the context object. This is by design to prevent loading too much. The proper solution is changing context customer
 * E.g. Customer -> Device will return all devices under Customer, but not devices belonging to child customers under Customer.
 *
 * Only gets groups that are supported type groups...
 *
 * @param groupDefiningObjectTypeId - Type that owns the group
 * @param supportedType - type of object sought. If this is Group then we only get groups with supported type group...
 * @param supportedTypeSubQueries - queries to run on each supported type (if they are returned)
 * @param includeSupportedTypeInstances - Option to include instances of supported type from the groups
 * @returns A NodeEdgeMapper containing source item and all supported type groups below leading to supportedTypes (+those supportedType items if includeSupportedTypeInstances is true) + all groups and items of definingTypes below source item
 */
const getItemTypeToGroupNestedDescendingQueryDef = ({ groupDefiningObjectTypeId, supportedType, supportedTypeSubQueries, includeSupportedTypeInstances, addIdPrefixes = true, }) => {
    const idPrefix = addIdPrefixes ? `${groupDefiningObjectTypeId}-${supportedType}-${iotv_v3_types_1.Traversal.DESCENDING}-` : '';
    const getGroupToSubGroupQuery = (supportedType) => new BranchingQueryDefinition_1.BranchingQueryDefinition({
        id: `${idPrefix}${supportedType}_groupToGroupQuery`,
        path: getGroupSubGroupParams(supportedType),
        subQueries: [],
    });
    /** ensures original items are returned in results */
    const apexQuery = new BranchingQueryDefinition_1.BranchingQueryDefinition({
        id: `${idPrefix}apexQuery`,
        path: [
            {
                adjacencyType: iotv_v3_types_1.AdjacentType.SUBJECT,
                objectTypeId: groupDefiningObjectTypeId,
            },
        ],
        subQueries: [],
    });
    /** eg Customer to Device Group */
    const definingTypeToSupportedTypeGroup = new BranchingQueryDefinition_1.BranchingQueryDefinition({
        id: `${idPrefix}definingTypeToSupportedTypeGroup`,
        path: [
            {
                objectTypeId: 'Group',
                adjacencyType: iotv_v3_types_1.AdjacentType.CHILD,
                edgeTypeId: (0, edgeTypeMatrixFns_1.getChildEdgeTypeId)(groupDefiningObjectTypeId, 'Group'),
                nodeFilters: getGroupSupportedTypeFilter(supportedType),
            },
        ],
        subQueries: [],
    });
    /** eg Customer to Customer Group */
    const definingTypeToDefiningTypeGroup = new BranchingQueryDefinition_1.BranchingQueryDefinition({
        id: `${idPrefix}definingTypeToDefiningTypeGroup`,
        path: [
            {
                objectTypeId: 'Group',
                adjacencyType: iotv_v3_types_1.AdjacentType.CHILD,
                edgeTypeId: (0, edgeTypeMatrixFns_1.getChildEdgeTypeId)(groupDefiningObjectTypeId, 'Group'),
                nodeFilters: getGroupSupportedTypeFilter(groupDefiningObjectTypeId),
            },
        ],
        subQueries: [],
    });
    /** eg Customer to Device (that can't happen, but something like Customer -> Property can */
    //console.log('S', supportedType)
    const definingTypeToSupportedType = (0, simple_1.getSimpleBranchingQuery)(groupDefiningObjectTypeId, supportedType, iotv_v3_types_1.AdjacentType.CHILD);
    /** eg Customer Group to Customer */
    const definingTypeGroupToDefiningType = new BranchingQueryDefinition_1.BranchingQueryDefinition({
        id: `${idPrefix}definingTypeGroupToDefiningType`,
        path: [
            {
                objectTypeId: groupDefiningObjectTypeId,
                adjacencyType: iotv_v3_types_1.AdjacentType.CHILD,
                edgeTypeId: (0, edgeTypeMatrixFns_1.getChildEdgeTypeId)('Group', groupDefiningObjectTypeId),
            },
        ],
        subQueries: [],
    });
    /** eg UserGroup allows Device Group */
    const groupAllowsGroup = new BranchingQueryDefinition_1.BranchingQueryDefinition({
        id: `${idPrefix}groupAllowsGroup`,
        path: [
            {
                objectTypeId: 'Group',
                adjacencyType: iotv_v3_types_1.AdjacentType.CHILD,
                edgeTypeId: EdgeTypeMatrix_1.edgeTypeMatrix.Group.Group.group_allows_group.edgeTypeId,
            },
        ],
        subQueries: [],
    });
    /** eg Customer Group to Customer Group */
    const definingTypeSubGroups = getGroupToSubGroupQuery(groupDefiningObjectTypeId);
    /** eg Device Group to Device Group */
    const supportedTypeSubGroups = getGroupToSubGroupQuery(supportedType);
    /** eg Device Group to Device  */
    // Only added if relevant param is true
    const supportedTypeGroupToSupportedType = new BranchingQueryDefinition_1.BranchingQueryDefinition({
        path: [
            {
                objectTypeId: supportedType,
                adjacencyType: iotv_v3_types_1.AdjacentType.CHILD,
                edgeTypeId: (0, edgeTypeMatrixFns_1.getChildEdgeTypeId)('Group', supportedType),
            },
        ],
        subQueries: [...(supportedTypeSubQueries ?? [])],
        id: `${idPrefix}${GROUP_SUPPORTED_TYPE_QUERY_KEY}`,
    });
    /** eg: Customer to Customer Groups */
    apexQuery.addSubQuery(definingTypeToDefiningTypeGroup);
    /** eg: Customer to Device Groups */
    apexQuery.addSubQuery(definingTypeToSupportedTypeGroup);
    apexQuery.addSubQuery(definingTypeToSupportedType);
    /** eg: Customer to Group, no matter the supported type */
    if (supportedType === 'Group')
        apexQuery.addSubQuery(definingTypeToSupportedTypeGroup);
    /** eg Customer Groups to sub Customer Groups and Customers */
    definingTypeToDefiningTypeGroup.addSubQuery(definingTypeSubGroups);
    definingTypeToDefiningTypeGroup.addSubQuery(definingTypeGroupToDefiningType);
    definingTypeToDefiningTypeGroup.addSubQuery(groupAllowsGroup);
    /** eg Customer sub groups to further nested sub groups (recursive), and their contained Customers */
    definingTypeSubGroups.addSubQuery(definingTypeSubGroups);
    definingTypeSubGroups.addSubQuery(groupAllowsGroup);
    definingTypeSubGroups.addSubQuery(definingTypeGroupToDefiningType);
    /** eg Customers to Customer Groups ( same as apex ) */
    definingTypeGroupToDefiningType.addSubQuery(definingTypeToDefiningTypeGroup);
    /** eg  Device Groups to sub Device Groups */
    definingTypeToSupportedTypeGroup.addSubQuery(supportedTypeSubGroups);
    definingTypeToSupportedTypeGroup.addSubQuery(groupAllowsGroup);
    /** eg Device sub groups to further nested sub groups (recursive) */
    supportedTypeSubGroups.addSubQuery(supportedTypeSubGroups);
    supportedTypeSubGroups.addSubQuery(groupAllowsGroup);
    if (includeSupportedTypeInstances) {
        /** eg  Device Groups to their contained Devices  */
        definingTypeToSupportedTypeGroup.addSubQuery(supportedTypeGroupToSupportedType);
        /** eg Device sub groups to their contained Devices */
        supportedTypeSubGroups.addSubQuery(supportedTypeGroupToSupportedType);
    }
    return apexQuery;
};
exports.getItemTypeToGroupNestedDescendingQueryDef = getItemTypeToGroupNestedDescendingQueryDef;
const getDescendingGroupRecuriveQuery = ({ items, groupDefiningObjectTypeId, supportedType, supportedTypeSubQueries, includeSupportedTypeInstances, }) => {
    const query = {
        topLevelQuery: (0, exports.getItemTypeToGroupNestedDescendingQueryDef)({
            groupDefiningObjectTypeId,
            supportedType,
            supportedTypeSubQueries,
            includeSupportedTypeInstances,
        }),
        contextObjects: items,
    };
    return query;
};
exports.getDescendingGroupRecuriveQuery = getDescendingGroupRecuriveQuery;
